import React from 'react';
import CandlestickChart from './CandlestickChart';
import LiveBox from './LiveBox';
import SettledBox from './SettledBox';
import TimeSlotRow from './TimeSlotRow';

/**
 * CoinColumn — One column per crypto asset on the Trading Floor.
 *
 * Layout (top to bottom):
 *   1. Graph (collapsible) — Birdeye historical + live data
 *   2. SettledBox — Last 2 completed slots with WIN/LOSS
 *   3. LiveBox — Active countdown (OPEN/bet/CLOSE 3-line)
 *   4. Betting List — Future time slots for placing bets
 */
const CoinColumn = ({
    coin,
    prices,
    priceHistory,
    timeSlots,
    countdown,
    bets,
    history,
    settlements,
    liveStartPrice,
    pools,
    isPaused,
    getSentimentRatio,
    getPayoutRatio,
    graphCollapsed,
    setGraphCollapsed,
    handleDotClick,
    handleBetChange,
    submitBet,
    setBets
}) => {
    const currentSlot = timeSlots[coin.id]?.[0];
    const currentPrice = prices[coin.id]?.usd;
    const priceDirection = prices[coin.id]?.direction || 'flat';
    const priceTick = prices[coin.id]?.tick || 0;
    const [flash, setFlash] = React.useState('');
    const [graphLight, setGraphLight] = React.useState(false);
    const [timeframe, setTimeframe] = React.useState('1m');
    const settledRef = React.useRef(null);

    // Flash green/red on price tick, reset after 300ms
    React.useEffect(() => {
        if (priceDirection === 'up') setFlash('flash-green');
        else if (priceDirection === 'down') setFlash('flash-red');
        else return;
        const t = setTimeout(() => setFlash(''), 300);
        return () => clearTimeout(t);
    }, [priceTick, priceDirection]);

    // Scroll to most recent on first load only
    const initialScrollDone = React.useRef(false);
    React.useEffect(() => {
        if (!initialScrollDone.current && settledRef.current && history && history.length > 0) {
            settledRef.current.scrollTop = settledRef.current.scrollHeight;
            initialScrollDone.current = true;
        }
    }, [history]);

    // Timeframe → interval + hoursBack mapping
    const TIMEFRAMES = {
        '1m': { interval: '1m', hoursBack: 2, label: '1m' },
        '5m': { interval: '5m', hoursBack: 8, label: '5m' },
        '15m': { interval: '15m', hoursBack: 24, label: '15m' },
        '1H': { interval: '1H', hoursBack: 168, label: '1H' },
        '4H': { interval: '4H', hoursBack: 720, label: '4H' },
        '1D': { interval: '1D', hoursBack: 8760, label: '1D' },
        '1W': { interval: '1W', hoursBack: 8760, label: '1W' },
    };

    const activeTf = TIMEFRAMES[timeframe];

    // Find the bet state for the current live slot
    const liveBetState = currentSlot ? bets[`${coin.id}_${currentSlot.id}`] : null;



    // Card direction based on price vs. session start (same logic as existing chart border)
    const cardDir = (currentPrice && liveStartPrice)
        ? (currentPrice >= liveStartPrice ? 'up' : 'down')
        : 'flat';

    return (
        <div className={`coin-card ${cardDir} min-w-0`}>

            {/* 1. Graph area — collapsible */}
            <div className={`flex flex-col transition-all duration-300 overflow-hidden border-b ${
                currentPrice && liveStartPrice
                    ? (currentPrice >= liveStartPrice ? 'border-[rgba(0,229,204,0.25)]' : 'border-[rgba(255,59,107,0.25)]')
                    : 'border-[#1e2333]'
                } ${graphCollapsed[coin.id] ? 'h-9' : 'h-[26%]'}`}>
                <div className="relative flex justify-between items-center px-3 h-9 shrink-0">
                    <div className="flex items-center gap-2">
                        <span className={`w-1.5 h-1.5 rounded-full animate-blink inline-block ${cardDir==='up'?'bg-[#00c896]':cardDir==='down'?'bg-[#ff3b6b]':'bg-gray-600'}`}></span>
                        <span className="uppercase tracking-widest text-[10px] font-black" style={{color:'var(--text-3)'}}>{coin.symbol}/USD</span>
                    </div>

                    {/* Live price — center of chart bar (replaces pool dropdown) */}
                    {currentPrice && (
                        <div className={`absolute left-1/2 -translate-x-1/2 z-20 transition-colors ${flash}`}>
                            <span
                                className="text-xl font-black text-white leading-none"
                                style={{ fontFamily: "'JetBrains Mono', monospace" }}
                            >
                                ${currentPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                        </div>
                    )}

                    <div className="flex items-center gap-1">
                        <select
                            value={timeframe}
                            onChange={(e) => setTimeframe(e.target.value)}
                            className="bg-gray-800/60 text-gray-300 text-[10px] font-mono rounded px-1 py-0.5 border border-gray-700/50 outline-none hover:border-gray-500 cursor-pointer appearance-none"
                            style={{ width: '36px' }}
                        >
                            {Object.keys(TIMEFRAMES).map(tf => (
                                <option key={tf} value={tf}>{tf}</option>
                            ))}
                        </select>
                        <button
                            onClick={() => setGraphLight(prev => !prev)}
                            className="text-gray-600 hover:text-gray-300 transition-colors text-[10px] leading-none w-5 h-5 flex items-center justify-center"
                            title={graphLight ? 'Dark mode' : 'Light mode'}
                        >
                            {graphLight ? '☾' : '☀'}
                        </button>
                        <button
                            onClick={() => setGraphCollapsed(prev => ({ ...prev, [coin.id]: !prev[coin.id] }))}
                            className="text-gray-600 hover:text-gray-300 transition-colors text-base leading-none w-5 h-5 flex items-center justify-center"
                        >
                            {graphCollapsed[coin.id] ? '+' : '−'}
                        </button>
                    </div>
                </div>

                {!graphCollapsed[coin.id] && (
                    <div className={`flex-1 px-0 pb-0 relative ${graphLight ? 'bg-white' : ''}`}>
                        <CandlestickChart
                            coinId={coin.id}
                            currentPrice={currentPrice}
                            theme={graphLight ? 'light' : 'dark'}
                            interval={activeTf.interval}
                            hoursBack={activeTf.hoursBack}
                        />
                    </div>
                )}
            </div>

            {/* 2. Settled Box — Last 3 completed results */}
            <div ref={settledRef} className="bg-gray-900/10">
                <SettledBox
                    coin={coin}
                    historyItems={history}
                    bets={bets}
                    settlements={settlements}
                    getPayoutRatio={getPayoutRatio}
                    getSentimentRatio={getSentimentRatio}
                    handleDotClick={handleDotClick}
                    handleBetChange={handleBetChange}
                    submitBet={submitBet}
                    setBets={setBets}
                />
            </div>

            {/* 3. Live Box — Active countdown */}
            <div>
                <LiveBox
                    coin={coin}
                    slot={currentSlot}
                    countdown={countdown}
                    liveStartPrice={liveStartPrice}
                    isPaused={isPaused}
                    betState={liveBetState}
                    getSentimentRatio={getSentimentRatio}
                    getPayoutRatio={getPayoutRatio}
                    handleDotClick={handleDotClick}
                    handleBetChange={handleBetChange}
                    submitBet={submitBet}
                    setBets={setBets}
                    currentPrice={currentPrice}
                    priceFlash={flash}
                    poolTotal={(() => {
                        const p = currentSlot ? pools[`${coin.id}_${currentSlot.id}`] : null;
                        return p ? (p.up + p.down) : null;
                    })()}
                />
            </div>

            {/* COLUMN HEADERS — above the time-slot betting list */}
            <div className="slot-col-header">
                <div className="min-w-[5rem] w-[5rem] pl-1">Time</div>
                <div className="flex flex-1 pr-5 items-center gap-0">
                    <div className="min-w-[45px]">Odds</div>
                    <div className="border-l border-[#1e2333] pl-2 min-w-[75px]">Credits</div>
                </div>
            </div>

            {/* 4. Betting List — Future time slots */}
            <div className="flex-1 overflow-y-auto overflow-x-hidden pt-0 pb-1 custom-scrollbar">
                {timeSlots[coin.id]?.slice(1).map((slot, index) => (
                    <TimeSlotRow
                        key={slot.id}
                        coinId={coin.id}
                        slot={slot}
                        isNextFive={index < 5}
                        isLocked={index === 0}
                        betState={bets[`${coin.id}_${slot.id}`]}
                        pool={pools[`${coin.id}_${slot.id}`]}
                        getSentimentRatio={getSentimentRatio}
                        getPayoutRatio={getPayoutRatio}
                        handleDotClick={handleDotClick}
                        handleBetChange={handleBetChange}
                        submitBet={submitBet}
                        setBets={setBets}
                    />
                ))}
            </div>
        </div>
    );
};

export default CoinColumn;
